import { checkFirstAndLastName } from '@shef/validators/dist/src/checkName';
import { ZIP_CODE_REGEX } from 'common/Constants';
import { ClientEvents } from 'common/events/ClientEvents';
import { isLeft, isRight } from 'fp-ts/lib/Either';
import { isNil } from 'lodash';
import React, { useEffect, useState } from 'react';
import { CurrentUserFieldsFragment, OAuthLoginType, OAuthMissingFieldsResult } from 'src/gqlReactTypings.generated.d';
import { useFormattedPhoneInput } from 'src/shared/hooks/useFormattedPhoneInput';
import { useTextInputCallback } from 'src/shared/hooks/useInputCallback';
import { isValidPhone } from 'src/shared/utils/PhoneUtils';
import { UserPreferences } from 'src/user-preferences/userPreferencesTypes';
import { useUserPreferences } from 'src/user-preferences/useUserPreferences';
import { useTracker } from '../../shared/hooks/useTracker';
import { useLoginOrSignUpWithOAuth } from '../oAuth/useLoginOrSignUpWithOAuth';
import { AuthActionButton } from './AuthActionButton';
import { useGateType } from './GateTypeContext';
import { OAuthConsent, OAuthErrorMessage, OAuthFieldset, OAuthNameFields, OAuthTextField } from './oAuthFormFields';

interface OAuthMissingFieldsFormProps {
  missingFields: OAuthMissingFieldsResult;
  token: string;
  type: OAuthLoginType;
  onSuccess: (isExistingUser: boolean, currentUser: CurrentUserFieldsFragment) => void;
  requireZipCode?: boolean;
  signUpEntryPoint?: string | null;
}

export const OAuthMissingFieldsForm: React.FC<OAuthMissingFieldsFormProps> = ({
  token,
  missingFields,
  onSuccess,
  type,
  requireZipCode,
  signUpEntryPoint,
}) => {
  const [nameErrorMessage, setNameErrorMessage] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [zipCodeErrorMessage, setZipCodeErrorMessage] = useState<string | undefined>();
  const [phoneErrorMessage, setPhoneErrorMessage] = useState<string | undefined>();
  const [firstName, onFirstNameChange] = useTextInputCallback(missingFields.firstName ?? '');
  const [lastName, onLastNameChange] = useTextInputCallback(missingFields.lastName ?? '');
  const [zipCode, onZipCodeChange] = useTextInputCallback('');
  const [displayPhone, onPhoneChange, rawPhone] = useFormattedPhoneInput();
  const [tosChecked, setTosChecked] = useState(false);
  const tracker = useTracker();
  const gateType = useGateType();

  const [, setPreference] = useUserPreferences();

  const onError = () => {
    setErrorMessage('Login failed. Please try again, or try using a different method.');
  };

  useEffect(() => {
    // This form is only used for apple missing fields
    tracker.track(ClientEvents.LOGIN_MODAL_SIGNUP_PAGE, {
      signUpEntryPoint,
      method: 'apple',
      gateType,
    });
  }, [tracker, signUpEntryPoint, gateType]);

  const { login } = useLoginOrSignUpWithOAuth({
    type,
    onError,
    onSuccess,
    requireZipCode,
    signUpEntryPoint,
  });

  const validateForm = () => {
    setErrorMessage(undefined);
    setNameErrorMessage(undefined);
    setPhoneErrorMessage(undefined);

    const nameCheck = checkFirstAndLastName({ firstName, lastName });
    if (isLeft(nameCheck)) {
      setNameErrorMessage(nameCheck.left);
    }

    const isZipCodeValid = !missingFields.zipCodeIsMissing || ZIP_CODE_REGEX.test(zipCode);
    if (!isZipCodeValid) {
      setZipCodeErrorMessage('Please enter a valid zip code');
    }

    const isPhoneValid = isValidPhone(rawPhone);
    if (!isPhoneValid) {
      setPhoneErrorMessage('Please enter a valid phone number');
    }

    if (!tosChecked && missingFields.needsToAcceptTermsOfService) {
      setErrorMessage('You must accept our Terms of Service to register');
    }

    return (
      isRight(nameCheck) && isZipCodeValid && isPhoneValid && (tosChecked || !missingFields.needsToAcceptTermsOfService)
    );
  };

  const handleSubmitForm = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const isFormValidated = validateForm();
    if (isFormValidated) {
      if (missingFields.zipCodeIsMissing) {
        setPreference(UserPreferences.ZIPCODE, zipCode);
      }
      // missing fields form is only for apple, google uses additional fields form
      tracker.track(ClientEvents.LOGIN_MODAL_SIGNUP_SUBMIT, { method: 'apple' });

      login(token, {
        firstName,
        lastName,
        zipCode,
        phone: rawPhone,
        hasAcceptedTermsOfService: isNil(missingFields.needsToAcceptTermsOfService) ? undefined : tosChecked,
      });
    }
  };

  // Theoretically this shouldn't be needed – not sure why enter isn't handling form submission like normal
  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitForm();
    }
  };

  return (
    <form onSubmit={handleSubmitForm} onKeyDown={handleKeyDown} data-cy='sign-up-form'>
      <OAuthFieldset>
        <OAuthNameFields
          firstName={firstName}
          lastName={lastName}
          onFirstNameChange={onFirstNameChange}
          onLastNameChange={onLastNameChange}
          errorMessage={nameErrorMessage}
          dataCyPrefix='sign-up'
        />
        {missingFields.zipCodeIsMissing && (
          <OAuthTextField
            label='Zip Code'
            name='zipCode'
            value={zipCode}
            onChange={onZipCodeChange}
            errorMessage={zipCodeErrorMessage}
            dataCy='sign-up-zip-code'
          />
        )}
        <OAuthTextField
          label='Phone Number'
          name='phone'
          value={displayPhone}
          onChange={onPhoneChange}
          errorMessage={phoneErrorMessage}
          dataCy='sign-up-phone'
        />
        {missingFields.needsToAcceptTermsOfService ? (
          <OAuthConsent
            checked={tosChecked}
            onChange={setTosChecked}
            dataCy='sign-up-tos'
            cta='Sign up'
            legacyLinkClassName='primary'
          />
        ) : null}

        {errorMessage ? <OAuthErrorMessage>{errorMessage}</OAuthErrorMessage> : null}
        <AuthActionButton value='Sign up' />
      </OAuthFieldset>
    </form>
  );
};
